import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "./AuthContext";
import { Base64 } from "js-base64";
import { isExpired, decodeToken } from "react-jwt";
import { useRequest } from "../hooks";
import { useMediaQuery } from "@mui/material";
import { UserDataProps } from "../interfaces/interfaces";

interface Props {
  children: JSX.Element | JSX.Element[];
}

const AuthProvider = ({ children }: Props) => {
  const { handleRequest } = useRequest();
  const navigate = useNavigate();
  const desktop = useMediaQuery("(min-width:900px)");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(desktop);
  const [idRole, setIdRole] = useState<null | number>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientIds, setClientIds] = useState<number[]>([]);
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    validateSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => setIsError(false), 3000);
  }, [isSuccess, isError]);

  useEffect(() => {
    setIsDrawerOpen(desktop);
    const tokenCoded = localStorage.getItem("info");
    if (tokenCoded) {
      hydrateUserFromToken(tokenCoded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop]);

  const hydrateUserFromToken = (token: string) => {
    const dataUser: UserDataProps | null = decodeToken(token);
    if (!dataUser) return null;

    setIdRole(dataUser.idRol);
    const assignedClients = dataUser.clients ?? [];
    const assignedClientIds =
      dataUser.clientIds ??
      (assignedClients.length
        ? assignedClients.map((client) => client.id)
        : dataUser.clientId
          ? [dataUser.clientId]
          : []);
    const fallbackClientId = assignedClientIds[0] ?? dataUser.clientId ?? null;
    setClientId(fallbackClientId);
    setClientIds(assignedClientIds);
    setClients(
      assignedClients.length
        ? assignedClients
        : fallbackClientId
          ? [{ id: fallbackClientId, name: "Client" }]
          : [],
    );
    setSelectedClientId(fallbackClientId);
    setUserName(dataUser.name);
    setUserEmail(dataUser.user);

    return dataUser;
  };

  const logoutHandler = async () => {
    setIsLoading(true);
    localStorage.clear();
    setIsLoggedIn(false);
    setIdRole(null);
    setClientId(null);
    setClientIds([]);
    setClients([]);
    setSelectedClientId(null);
    setUserName(null);
    setUserEmail(null);
    navigate("/");
    setIsLoading(false);
  };

  const loginHandler = (username: string, pass: string) => {
    setIsLoading(true);
    try {
      const loop1Pass = Base64.encode(pass);
      const loop2Pass = Base64.encode(loop1Pass);
      const data = { user: username, password: loop2Pass };
      const options: RequestInit = {
        method: "POST",
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/json" },
      };

      handleRequest({ endpoint: "login", options })
        .then((response) => {
          if (response.data.token) {
            localStorage.setItem("info", response.data.token);
            const dataUser = hydrateUserFromToken(response.data.token);

            setIsLoggedIn(true);
            setIsSuccess(true);
            setIsLoading(false);
            navigate(dataUser?.idRol === 3 ? "/proposal" : "/home");
          } else {
            setIsLoading(false);
            setIsError(true);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setIsError(true);
        });
    } catch (e) {
      setIsLoading(false);
      setIsError(true);
    }
  };

  const validateSession = () => {
    const token = localStorage.getItem("info");
    if (token && validateToken()) {
      hydrateUserFromToken(token);
      setIsLoggedIn(true);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      localStorage.clear();
      setIsLoggedIn(false);
      setIsLoading(false);
    }
  };

  const validateToken = (): boolean => {
    const token = localStorage.getItem("info");
    if (!token) return false;
    return !isExpired(token);
  };

  const setToken = (token: string) => {
    localStorage.setItem("info", token);
    setIsLoggedIn(true);
    setIsSuccess(true);
    setIsLoading(false);
  };

  const setUser = (user: any) => {};

  const fetchUser = (token: string) => {
    hydrateUserFromToken(token);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isSuccess,
        isError,
        isLoading,
        isDrawerOpen,
        desktop,
        idRole,
        clientId,
        clientIds,
        clients,
        selectedClientId,
        setSelectedClientId,
        userName,
        userEmail,
        onMoveDrawer: (action: boolean) => setIsDrawerOpen(action),
        onLogin: loginHandler,
        onLogout: logoutHandler,
        validateToken,
        setToken,
        setUser,
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
